import { useEffect, useState } from "react";
import { Box, BoxProps, Collapse, styled, useMediaQuery } from "@mui/material";
import { ExpandMore } from "@mui/icons-material";

type ResponsiveSectionProps = Omit<BoxProps, "title"> & { title: React.ReactNode };

type HeaderProps = {
  isLargeScreen?: boolean;
};

const Header = styled(Box, {
  shouldForwardProp: (prop) => prop !== "isLargeScreen",
})<HeaderProps>(({ theme, isLargeScreen }) => ({
  display: "grid",
  gridTemplateColumns: "1fr auto",
  alignItems: "center",
  padding: `${theme.spacing(1)} ${theme.spacing(2)}`,
  ...(isLargeScreen && {
    paddingBottom: 0,
    fontSize: "small",
  }),
  ...(!isLargeScreen && {
    paddingBlock: theme.spacing(1.5),
    ":hover": {
      cursor: "pointer",
    },
  }),
}));

type ExpandIconProps = {
  isExpanded?: boolean;
  isLargeScreen?: boolean;
};

const ExpandIcon = styled(ExpandMore, {
  shouldForwardProp: (prop) => prop !== "isExpanded" && prop !== "isLargeScreen",
})<ExpandIconProps>(({ theme, isExpanded, isLargeScreen }) => ({
  rotate: isExpanded ? "180deg" : "0deg",
  transition: "rotate 0.2s",
  color: theme.palette.text.secondary,
  ...(isLargeScreen && { display: "none" }),
}));

const ResponsiveSection: React.FC<ResponsiveSectionProps> = (props) => {
  const { title, children, ...restProps } = props;

  const isLargeScreen = useMediaQuery((theme) => theme.breakpoints.up("md"));
  const [isExpanded, setIsExpanded] = useState(isLargeScreen);
  useEffect(() => setIsExpanded(isLargeScreen), [isLargeScreen]);

  return (
    <Box {...restProps}>
      <Header
        isLargeScreen={isLargeScreen}
        onClick={() => !isLargeScreen && setIsExpanded(!isExpanded)}
      >
        {title}
        <ExpandIcon isExpanded={isExpanded} isLargeScreen={isLargeScreen} />
      </Header>

      <Collapse in={isExpanded}>{children}</Collapse>
    </Box>
  );
};

export default ResponsiveSection;
